/*
 *   File : goal-retirement-snapshot.js
 *   Author : https://evoqins.com
 *   Description : Retirement snapshot modal with the plan, 10% step-up and goal needed tabs
 *   Integrations : NA
 *   Version : 1.0.0
*/

// *********** React Core ***************//
import { useEffect, useState } from "react";

// *********** Components ***************//
import { PrimaryButton } from "../Buttons";
import { _formatCorpus, _formatToRupees } from "../../Helper";

// *********** Assets ***************//
import Close from "../../Assets/Images/close-btn.svg";
import BackButton from "../../Assets/Images/GoalNew/modal-back-btn.svg";

const MODAL_ID = "goal-retirement-snapshot-modal";

// Tab content from the retirement corpus (API) and the SIP projections worked out on the page
// TODO: the copy for the not-reached step-up tab and for the goal needed tab is not in the design, confirm with product
const _getSnapshotTabs = (plan) => {
    const is_plan_funded = plan.projected_corpus >= plan.target_corpus;
    const is_step_up_funded = plan.step_up_corpus >= plan.target_corpus;

    // The goal needed tab follows the step-up switch of the page
    let goal_status_title = `Reach it at ₹${_formatToRupees(plan.required_flat_sip)} / month`;
    let goal_status_note = "If your SIP stays flat";
    if (plan.is_step_up_applied) {
        goal_status_title = `Start at ₹${_formatToRupees(plan.required_step_up_sip)} / month`;
        goal_status_note = `With a ${plan.step_up_rate}% annual SIP step-up`;
    }
    if (is_plan_funded) {
        goal_status_title = "You’re already there";
    }

    return [
        {
            id: 1,
            label: "Your plan",
            is_goal_met: is_plan_funded,
            corpus_label: "Projected corpus",
            corpus_value: `₹${_formatCorpus(plan.projected_corpus)}`,
            corpus_note: "This is where your current SIP and savings take you by retirement.",
            status_title: is_plan_funded ? "Goal reached with your current SIP" : `₹${_formatToRupees(plan.additional_flat_sip)} more / month`,
            status_note: is_plan_funded ? "If your SIP stays flat" : "Additional flat monthly SIP required to reach 100% of your target corpus.",
        },
        {
            id: 2,
            label: `${plan.step_up_rate}% step-up`,
            is_goal_met: is_step_up_funded,
            corpus_label: "Projected corpus with step-up",
            corpus_value: `₹${_formatCorpus(plan.step_up_corpus)}`,
            corpus_note: is_step_up_funded ? `Your current starting SIP becomes enough when it grows ${plan.step_up_rate}% each year` : `This is where your SIP takes you when it grows ${plan.step_up_rate}% each year.`,
            status_title: is_step_up_funded ? "Goal reached with your current SIP" : `Start at ₹${_formatToRupees(plan.required_step_up_sip)} / month`,
            status_note: `Then increase the SIP by ${plan.step_up_rate}% each year`,
        },
        {
            id: 3,
            label: "Goal needed",
            is_goal_met: true,
            corpus_label: "Corpus you need",
            corpus_value: `₹${_formatCorpus(plan.target_corpus)}`,
            corpus_note: is_plan_funded ? "Your current plan reaches the target. The Green state is the reward." : "This is the corpus you need to fund your retirement income.",
            status_title: goal_status_title,
            status_note: goal_status_note,
        },
    ];
};

function GoalRetirementSnapshotModal(props) {

    const [activeTabId, setActiveTabId] = useState(1);

    const snapshotTabs = _getSnapshotTabs(props.plan);
    const activeTab = snapshotTabs.find((tab) => tab.id === activeTabId);

    const detailRows = [
        { label: "Years to retirement", value: props.plan.years, unit: "yrs" },
        { label: "Desired income", value: `₹${_formatToRupees(props.desiredIncome)}` },
        { label: "Current SIP", value: `₹${_formatToRupees(props.currentSip)}` },
        { label: "Assumed return", value: `${props.assumedReturn}%`, unit: "p.a" },
    ];

    // Lets the page know when the modal closes by any route (buttons, backdrop or Esc)
    useEffect(() => {
        const modal_element = document.getElementById(MODAL_ID);
        modal_element.addEventListener("hidden.bs.modal", props.onClose);

        return () => {
            modal_element.removeEventListener("hidden.bs.modal", props.onClose);
        };
    }, [props.onClose]);

    const _handleSelectTab = (id) => {
        setActiveTabId(id);
    };

    return (
        <div className="modal fade e-manage-modal goal-retirement-snapshot-modal"
            id={MODAL_ID}
            tabIndex="-1"
            aria-labelledby={`${MODAL_ID}-label`}
            aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content e-bg-lotion e-border-radius-24 e-modal-box-shadow">

                    {/* Header */}
                    <div className="modal-header border-0 d-flex align-items-center gap-3 p-4">
                        <button type="button"
                            className="d-flex p-0 no-border no-outline e-bg-transparent cursor-pointer"
                            data-bs-dismiss="modal"
                            aria-label="Back">
                            <img src={BackButton}
                                width={32}
                                height={32}
                                draggable="false"
                                alt="" />
                        </button>
                        <h6 id={`${MODAL_ID}-label`}
                            className="mb-0 e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-18 line-height-32px">
                            Retirement snapshot
                        </h6>
                        <button type="button"
                            id={`${MODAL_ID}-close`}
                            className="d-flex p-0 ms-auto no-border no-outline e-bg-transparent cursor-pointer"
                            data-bs-dismiss="modal"
                            aria-label="Close">
                            <img src={Close}
                                width={32}
                                height={32}
                                draggable="false"
                                alt="" />
                        </button>
                    </div>

                    <div className="modal-body px-3 px-sm-4 pt-0 pb-3">
                        <p className="mb-0 e-text-jet-gray e-primary-font-inter e-font-weight-400 e-font-12 line-height-16px">
                            Your plan at a glance
                        </p>
                        <h2 className="mb-0 e-text-eerie-black e-primary-font-inter e-font-weight-600 e-font-16 line-height-24px">
                            Your target, without the intimidation.
                        </h2>

                        {/* Tabs */}
                        <ul className="d-flex gap-3 list-unstyled mt-3 margin-20px-bottom"
                            role="tablist">
                            {
                                snapshotTabs.map((tab) => (
                                    <li key={tab.id} className="flex-fill" role="presentation">
                                        <button type="button"
                                            id={`${MODAL_ID}-tab-${tab.id}`}
                                            className={`goal-tab ${tab.id === activeTabId ? "active" : ""} w-100 e-border-radius-8 padding-10px-tb cursor-pointer no-outline e-primary-font-inter e-font-weight-500 e-font-12 line-height-24px`}
                                            role="tab"
                                            aria-selected={tab.id === activeTabId}
                                            aria-controls={`${MODAL_ID}-panel`}
                                            onClick={() => _handleSelectTab(tab.id)}>
                                            {tab.label}
                                        </button>
                                    </li>
                                ))
                            }
                        </ul>

                        {/* Tab content */}
                        <div id={`${MODAL_ID}-panel`}
                            className={activeTab.is_goal_met ? "goal-snapshot-met" : "goal-snapshot-gap"}
                            role="tabpanel"
                            aria-labelledby={`${MODAL_ID}-tab-${activeTab.id}`}>
                            <article className="goal-snapshot-corpus e-border-radius-16 padding-12px-all">
                                <p className="mb-1 e-text-jet-gray e-primary-font-inter e-font-weight-400 e-font-12 line-height-16px">
                                    {activeTab.corpus_label}
                                </p>
                                <p className="goal-snapshot-corpus-value mb-0 e-text-black e-primary-font-inter e-font-weight-800 e-font-14 line-height-16px">
                                    {activeTab.corpus_value}
                                </p>
                                <div className="goal-snapshot-divider margin-12px-top margin-12px-bottom"></div>
                                <p className="mb-0 e-text-dim-ash e-primary-font-inter e-font-weight-400 e-font-10 line-height-14px">
                                    {activeTab.corpus_note}
                                </p>
                            </article>

                            <article className="goal-snapshot-status e-border-radius-16 padding-12px-all margin-12px-top">
                                <p className="goal-snapshot-status-label mb-1 e-primary-font-inter e-font-weight-400 e-font-10 line-height-14px">
                                    Plan status
                                </p>
                                <p className="goal-snapshot-status-title mb-1 e-primary-font-inter e-font-weight-800 e-font-14 line-height-16px">
                                    {activeTab.status_title}
                                </p>
                                <p className="goal-snapshot-status-note mb-0 e-primary-font-inter e-font-weight-400 e-font-10 line-height-14px">
                                    {activeTab.status_note}
                                </p>
                            </article>
                        </div>

                        {/* Plan details */}
                        <dl className="e-bg-white e-border-1px-anti-flash-gray e-border-radius-16 e-box-shadow-soft-card d-flex flex-column gap-3 padding-12px-all margin-12px-top mb-0">
                            {
                                detailRows.map((row) => (
                                    <div key={row.label} className="d-flex justify-content-between align-items-center gap-2 e-font-12 line-height-16px e-primary-font-inter">
                                        <dt className="e-text-dim-ash e-font-weight-400">
                                            {row.label}
                                        </dt>
                                        <dd className="d-flex gap-1 mb-0">
                                            <span className="e-text-black e-font-weight-600">{row.value}</span>
                                            {
                                                row.unit &&
                                                <span className="e-text-dim-ash e-font-weight-500">{row.unit}</span>
                                            }
                                        </dd>
                                    </div>
                                ))
                            }
                        </dl>
                    </div>

                    {/* Footer */}
                    <div className="modal-footer border-0 e-bg-white px-3 px-sm-4 pt-2 pb-4">
                        <PrimaryButton name="Start closing the gap with IFSH"
                            className="w-100 m-0 padding-12px-top padding-12px-bottom"
                            loading={props.isStarting}
                            disabled={props.isStarting}
                            onPress={props.onStart} />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default GoalRetirementSnapshotModal;
